import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { HumanTaskDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { relativeTime } from "@/lib/format";
import { Btn } from "@/design/primitives";
import {
  ALL_PROJECTS,
  ProjectFilterTabs,
  filterByProject,
  projectGroups,
  resolveProjectFilter,
} from "@/pages/decisions/project-filter";

const URGENCY_META: Record<string, { label: string; color: string; border: string }> = {
  URGENT: {
    label: "KRITICKÉ",
    color: "var(--critical)",
    border: "color-mix(in srgb, var(--critical) 40%, transparent)",
  },
  HIGH: {
    label: "VYSOKÉ",
    color: "var(--warning)",
    border: "color-mix(in srgb, var(--warning) 30%, transparent)",
  },
  NORMAL: {
    label: "BĚŽNÉ",
    color: "var(--accent)",
    border: "color-mix(in srgb, var(--accent) 15%, transparent)",
  },
  LOW: {
    label: "NÍZKÉ",
    color: "var(--text-secondary)",
    border: "color-mix(in srgb, var(--accent) 12%, transparent)",
  },
};

/**
 * Úkoly pro člověka na mobilu.
 *
 * Ze všech čtyř schránek je tahle nejvíc mobilní: „zkontroluj vzhled
 * a diakritiku na třech vytištěných páskách" ownera zastihne u tiskárny, ne
 * u počítače. Detail proto vede rovnou k jedinému, co se po něm chce — napsat
 * nález. Žádné Schválit/Zamítnout: pásky se schválit nedají.
 */
export function MobileHumanTasks() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["human-tasks"], queryFn: () => api.humanTasks() });
  const [rawProjectFilter, setProjectFilter] = useState<string>(ALL_PROJECTS);
  const [openId, setOpenId] = useState<string | null>(null);
  /** Naposledy uzavřený úkol — 8 s na vrácení, palec je nepřesný. */
  const [undoId, setUndoId] = useState<string | null>(null);
  const [undoMsg, setUndoMsg] = useState<string>("");
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const armUndo = (id: string, msg: string) => {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    setUndoId(id);
    setUndoMsg(msg);
    undoTimer.current = setTimeout(() => setUndoId(null), 8000);
  };

  const completeMutation = useMutation({
    mutationFn: ({ id, finding }: { id: string; finding: string }) =>
      api.completeHumanTask(id, { finding }),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["human-tasks"] });
      setOpenId(null);
      armUndo(variables.id, cs.humanTasks.doneToast(data.unblockedCount));
    },
  });
  const dismissMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.dismissHumanTask(id, { reason }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["human-tasks"] });
      setOpenId(null);
      armUndo(variables.id, cs.humanTasks.dismissedToast);
    },
  });
  const undoMutation = useMutation({
    mutationFn: (id: string) => api.reopenHumanTask(id),
    onSuccess: () => {
      setUndoId(null);
      void queryClient.invalidateQueries({ queryKey: ["human-tasks"] });
    },
  });

  const allItems = query.data?.items ?? [];
  const projectFilter = resolveProjectFilter(projectGroups(allItems), rawProjectFilter);
  const items = filterByProject(allItems, projectFilter);

  const openItem = items.find((i) => i.id === openId) ?? null;
  if (openItem) {
    return (
      <MobileHumanTaskDetail
        item={openItem}
        index={items.indexOf(openItem) + 1}
        total={items.length}
        busy={completeMutation.isPending || dismissMutation.isPending}
        onBack={() => setOpenId(null)}
        onComplete={(finding) => completeMutation.mutate({ id: openItem.id, finding })}
        onDismiss={(reason) => dismissMutation.mutate({ id: openItem.id, reason })}
      />
    );
  }

  return (
    <div className="af-animate-fade" data-testid="mobile-human-tasks">
      <div className="mx-1 mb-3 flex items-baseline justify-between">
        <span className="af-display text-[16px] font-light" style={{ color: "var(--text-primary)" }}>
          {cs.humanTasks.title}
        </span>
        <span className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {cs.mobile.inboxWaiting(items.length)}
        </span>
      </div>

      <ProjectFilterTabs
        items={allItems}
        value={projectFilter}
        onChange={setProjectFilter}
        className="mx-1 mb-3"
        testIdPrefix="mobile-human-tasks"
      />

      {undoId ? (
        <div
          className="mb-2.5 flex items-center justify-between gap-3 px-3.5 py-2.5"
          style={{
            borderRadius: "var(--radius-box)",
            border: "1px solid color-mix(in srgb, var(--positive) 30%, transparent)",
          }}
          data-testid="mobile-human-task-undo-bar"
        >
          <span className="text-[12px]" style={{ color: "var(--positive)" }}>
            ✓ {undoMsg}
          </span>
          <button
            type="button"
            className="cursor-pointer whitespace-nowrap px-3 py-1.5 text-[11px] font-bold tracking-[0.1em] disabled:opacity-40"
            style={{
              borderRadius: "var(--radius-box)",
              border: "1px solid color-mix(in srgb, var(--warning) 45%, transparent)",
              color: "var(--warning)",
            }}
            disabled={undoMutation.isPending}
            onClick={() => undoMutation.mutate(undoId)}
            data-testid="mobile-human-task-undo"
          >
            ↩ {cs.humanTasks.undo}
          </button>
        </div>
      ) : null}

      {items.map((item) => {
        const meta = URGENCY_META[item.urgency] ?? URGENCY_META.NORMAL!;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => setOpenId(item.id)}
            className="af-animate-fade mb-2.5 block w-full p-3.5 text-left"
            style={{
              borderRadius: "var(--radius-card)",
              border: `1px solid ${meta.border}`,
              background: "var(--surface)",
            }}
            data-testid="mobile-human-task-card"
          >
            <div className="af-mono mb-1.5 flex flex-wrap justify-between gap-x-2 gap-y-1 text-[10px] tracking-[0.14em]">
              <span style={{ color: meta.color }}>
                {meta.label}
                {item.blocksProject ? ` · ${cs.humanTasks.blocksProjectTag}` : ""}
              </span>
              <span style={{ color: "var(--text-tertiary)" }}>
                {item.projectName?.toUpperCase() ?? "FACTORY"} · {relativeTime(item.createdAt)}
              </span>
            </div>
            <div className="af-card-title mb-2 leading-snug">{item.title}</div>
            {item.expectedFinding ? (
              <div
                className="mb-2 px-3 py-2 text-[12px] leading-relaxed"
                style={{
                  borderLeft: "2px solid color-mix(in srgb, var(--positive) 40%, transparent)",
                  background: "var(--positive-subtle)",
                  color: "var(--text-secondary)",
                }}
              >
                {item.expectedFinding}
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-2 text-[12px]">
              <span style={{ color: "var(--text-tertiary)" }}>
                {item.blockedTasks.length
                  ? `⛔ blokuje ${item.blockedTasks.length} tasků`
                  : item.whyHuman || "—"}
              </span>
              <span className="af-mono text-[11px]" style={{ color: "var(--accent)" }}>
                {cs.mobile.writeFinding} →
              </span>
            </div>
          </button>
        );
      })}

      {items.length === 0 && !query.isLoading ? (
        <div
          className="mt-5 px-5 py-8 text-center"
          style={{ border: "1px dashed color-mix(in srgb, var(--positive) 30%, transparent)" }}
          data-testid="mobile-human-tasks-empty"
        >
          <div className="af-card-title" style={{ color: "var(--positive)" }}>
            ✓ {cs.humanTasks.emptyTitle}
          </div>
          <div className="mt-2 text-[11px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {cs.humanTasks.emptyNote}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Detail úkolu. Hlavní akce je textové pole s nálezem — je to obsah, který
 * dostane worker, ne potvrzení, že se něco stalo. Proto je tlačítko zamčené,
 * dokud něco nenapíšeš.
 */
function MobileHumanTaskDetail({
  item,
  index,
  total,
  busy,
  onBack,
  onComplete,
  onDismiss,
}: {
  item: HumanTaskDTO;
  index: number;
  total: number;
  busy: boolean;
  onBack: () => void;
  onComplete: (finding: string) => void;
  onDismiss: (reason: string) => void;
}) {
  const meta = URGENCY_META[item.urgency] ?? URGENCY_META.NORMAL!;
  const [finding, setFinding] = useState("");
  const [dismissOpen, setDismissOpen] = useState(false);

  return (
    <div className="af-animate-fade pb-24" data-testid="mobile-human-task-detail">
      <div className="mb-3 flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex h-11 w-11 items-center justify-center text-[16px]"
          style={{ color: "var(--text-secondary)" }}
          aria-label={cs.common.back}
          data-testid="mobile-human-task-back"
        >
          ←
        </button>
        <span className="af-card-title">{cs.humanTasks.title}</span>
        <span className="af-mono ml-auto text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {index} / {total}
        </span>
      </div>

      <div className="af-mono mb-2 flex flex-wrap items-center gap-2 text-[10px] tracking-[0.14em]">
        <span
          className="px-2.5 py-1"
          style={{
            borderRadius: "var(--radius-pill)",
            background: "var(--critical-bg)",
            color: meta.color,
          }}
        >
          {meta.label}
        </span>
        {item.blocksProject ? (
          <span
            className="px-2.5 py-1"
            style={{
              borderRadius: "var(--radius-pill)",
              background: "var(--critical-bg)",
              color: "var(--critical)",
            }}
          >
            {cs.humanTasks.blocksProjectTag}
          </span>
        ) : null}
        <span style={{ color: "var(--text-tertiary)" }}>
          {item.projectName?.toUpperCase() ?? "FACTORY"} · {relativeTime(item.createdAt)}
        </span>
      </div>

      <h1
        className="af-display mb-4 text-[20px] font-bold leading-snug"
        style={{ color: "var(--text-primary)" }}
      >
        {item.title}
      </h1>

      {item.context ? (
        <div
          className="mb-3 p-3.5"
          style={{
            borderRadius: "var(--radius-box)",
            border: "1px solid var(--border-default)",
            background: "var(--surface)",
          }}
        >
          <div className="af-label-dim mb-1.5">{cs.humanTasks.whatToDo}</div>
          <p
            className="whitespace-pre-wrap text-[13px] leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            {item.context}
          </p>
        </div>
      ) : null}

      {item.expectedFinding ? (
        <div
          className="mb-3 p-3.5"
          style={{
            borderRadius: "var(--radius-box)",
            border: "1px solid var(--positive-border)",
            background: "var(--positive-subtle)",
          }}
        >
          <div className="af-label-dim mb-1.5">{cs.humanTasks.expectedFinding}</div>
          <p className="text-[13px] font-semibold leading-relaxed" style={{ color: "var(--text-primary)" }}>
            {item.expectedFinding}
          </p>
        </div>
      ) : null}

      {item.whyHuman ? (
        <div className="mb-3 px-1 text-[12px] leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
          {cs.humanTasks.whyHuman}: {item.whyHuman}
        </div>
      ) : null}

      <div className="af-label-dim mb-1.5">
        {dismissOpen ? cs.humanTasks.dismissLabel : cs.humanTasks.findingLabel}
      </div>
      <textarea
        className="mb-2 w-full resize-y p-3 text-[13px] outline-none"
        style={{
          borderRadius: "var(--radius-control)",
          border: `1px solid ${dismissOpen ? "var(--critical-border)" : "var(--border-field)"}`,
          background: "var(--surface-inset)",
          color: "var(--text-primary)",
          minHeight: 96,
        }}
        placeholder={cs.humanTasks.findingPlaceholder}
        maxLength={4000}
        value={finding}
        onChange={(e) => setFinding(e.target.value)}
        data-testid="mobile-human-task-finding"
      />
      <div className="mb-3 px-1 text-[11px] leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
        {cs.humanTasks.findingHint}
      </div>

      {/* akce dole — v dosahu palce */}
      <div
        className="fixed inset-x-0 bottom-[64px] z-30 flex gap-2 px-3.5 py-3"
        style={{
          background: "var(--surface-overlay)",
          borderTop: "1px solid var(--border-default)",
          backdropFilter: "var(--surface-blur)",
          paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))",
        }}
      >
        <Btn
          variant={dismissOpen ? "danger" : "primary"}
          size="lg"
          className="flex-1"
          disabled={busy || finding.trim().length < 2}
          onClick={() => (dismissOpen ? onDismiss(finding.trim()) : onComplete(finding.trim()))}
          data-testid="mobile-human-task-submit"
        >
          {dismissOpen ? cs.humanTasks.dismiss : cs.humanTasks.submit}
        </Btn>
        <Btn
          variant={dismissOpen ? "primary" : "danger"}
          size="lg"
          disabled={busy}
          onClick={() => setDismissOpen((v) => !v)}
          data-testid="mobile-human-task-toggle-dismiss"
        >
          {dismissOpen ? "↩" : "✕"}
        </Btn>
      </div>
    </div>
  );
}
